import { useState, useEffect, type ChangeEvent, type FormEvent } from "react";
import { setError } from "../../AppSlice";
import { useAppDispatch } from "../../redux";
import type { IModalProps } from "../../types";
import { changePasswordAction } from "./DashboardSlice";
import { Close, Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Modal,
  Button,
  Toolbar,
  Tooltip,
  IconButton,
  Typography,
  InputAdornment,
  OutlinedInput,
  InputLabel,
} from "@mui/material";

export default function ChangePasswordModal(props: IModalProps) {
  const { isOpen, success, styles, handleClose } = props;

  const dispatch = useAppDispatch();
  const [showCurrentPass, setShowCurrentPass] = useState(false);
  const [showNewPass, setshowNewPass] = useState(false);
  const [showRepPass, setshowRepPass] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  useEffect(() => {
    if (success) {
      setCurrentPassword("");
      setNewPassword("");
      setRepeat("");
      handleClose();
    }
  }, [success, handleClose]);

  useEffect(() => {
    if (!isOpen) {
      setCurrentPassword("");
      setNewPassword("");
      setRepeat("");
    }
  }, [isOpen]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.currentTarget;

    switch (name) {
      case "current": {
        return setCurrentPassword(value);
      }

      case "new": {
        return setNewPassword(value);
      }

      case "repeat": {
        return setRepeat(value);
      }
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (newPassword !== repeat) return dispatch(setError("Passwords do not match"));

    await dispatch(changePasswordAction({ currentPassword, password: newPassword }));
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      closeAfterTransition
      slotProps={{
        backdrop: {
          timeout: 500,
        },
      }}
    >
      <Box sx={styles.modalRoot}>
        <Tooltip title="Close" sx={styles.close}>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Tooltip>

        <Toolbar>
          <Typography variant="h6" component="h2">
            Change Password
          </Typography>
        </Toolbar>

        <Box component="form" onSubmit={handleSubmit}>
          <InputLabel>Current Password</InputLabel>

          <OutlinedInput
            required
            fullWidth
            id="outlined-adornment-password"
            name="current"
            value={currentPassword}
            onChange={handleInputChange}
            type={showCurrentPass ? "text" : "password"}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowCurrentPass(!showCurrentPass)}
                  edge="end"
                >
                  {showCurrentPass ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
          />
          <InputLabel>New Password*</InputLabel>
          <OutlinedInput
            required
            fullWidth
            name="new"
            value={newPassword}
            onChange={handleInputChange}
            type={showNewPass ? "text" : "password"}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setshowNewPass(!showNewPass)}
                  edge="end"
                >
                  {showNewPass ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
          />
          <InputLabel>Repeat Password*</InputLabel>

          <OutlinedInput
            required
            fullWidth
            label="Repeat Password"
            name="repeat"
            value={repeat}
            onChange={handleInputChange}
            type={showRepPass ? "text" : "password"}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setshowRepPass(!showRepPass)}
                  edge="end"
                >
                  {showRepPass ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
          />

          <Typography sx={{ mt: 2 }}>
            * Passwords must be at least 8 characters long, and contain at least one number, lowercase letter, and
            uppercase letter.
          </Typography>

          <Button type="submit" fullWidth color="primary" variant="contained" sx={{ mt: 2 }}>
            Change Password
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
